import React, { useState, useEffect, useCallback } from 'react';
import {
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  FileText,
  Eye,
  AlertCircle,
  Copy,
  Check,
  Zap,
  GitCompare,
  CheckCheck,
  ShieldCheck
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { trimInvoiceNumber } from '@/utils/invoiceUtils';

export const AdminRecoveryConsole = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Diagnostics Modal State
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [selectedDiagnostics, setSelectedDiagnostics] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Single-event Recovery State
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
  const [eventToRecover, setEventToRecover] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);

  // Reconciliation Modal State (Phase 3.3)
  const [reconModalOpen, setReconModalOpen] = useState(false);
  const [reconEvent, setReconEvent] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconPreview, setReconPreview] = useState(null);
  const [isReconciling, setIsReconciling] = useState(false);

  // Batch Lease Recovery State
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [isBatchRecovering, setIsBatchRecovering] = useState(false);

  // Batch Reconciliation State
  const [batchReconModalOpen, setBatchReconModalOpen] = useState(false);
  const [isBatchReconciling, setIsBatchReconciling] = useState(false);

  // Fetch outbox events from backend
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      const res = await api.get('/api/erp/outbox', { params });
      if (res.data?.success) {
        setEvents(res.data.data || []);
      } else {
        setEvents([]);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch ERP outbox events';
      toast.error(errMsg);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Open Diagnostics Modal
  const handleOpenDiagnostics = async (event) => {
    setDiagnosticsOpen(true);
    setDiagnosticsLoading(true);
    setSelectedDiagnostics(null);
    setCopiedKey(false);
    try {
      const res = await api.get(`/api/erp/outbox/${event.id}`);
      if (res.data?.success) {
        setSelectedDiagnostics(res.data.data);
      } else {
        setSelectedDiagnostics(event);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to fetch diagnostics';
      toast.error(errMsg);
      setSelectedDiagnostics(event);
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  // Open Recovery Confirmation Dialog
  const handleInitiateRecover = (event) => {
    setEventToRecover(event);
    setRecoveryModalOpen(true);
  };

  // Execute Controlled Recovery for single event
  const handleExecuteRecover = async () => {
    if (!eventToRecover) return;
    setIsRecovering(true);
    try {
      const res = await api.post(`/api/erp/outbox/${eventToRecover.id}/recover`);
      if (res.data?.success) {
        toast.success(res.data.message || `Outbox event #${eventToRecover.id} recovered successfully!`);
        setRecoveryModalOpen(false);
        setEventToRecover(null);
        await fetchEvents();
      } else {
        toast.error(res.data?.message || 'Recovery attempt failed');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to execute recovery';
      toast.error(errMsg);
    } finally {
      setIsRecovering(false);
    }
  };

  // Open Reconciliation Preview Dialog (Phase 3.3)
  const handleOpenReconcile = async (event) => {
    setReconEvent(event);
    setReconModalOpen(true);
    setReconLoading(true);
    setReconPreview(null);
    try {
      const res = await api.get(`/api/erp/outbox/${event.id}/reconcile`);
      if (res.data?.success) {
        setReconPreview(res.data.data);
      } else {
        setReconPreview({ status: 'ERROR', message: res.data?.message || 'Reconciliation check failed' });
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to check reconciliation status';
      setReconPreview({ status: 'ERROR', message: errMsg });
    } finally {
      setReconLoading(false);
    }
  };

  // Execute Controlled Reconciliation (Phase 3.3)
  const handleExecuteReconcile = async () => {
    if (!reconEvent) return;
    setIsReconciling(true);
    try {
      const res = await api.post(`/api/erp/outbox/${reconEvent.id}/reconcile`, {
        auto_recover_if_missing: false
      });
      if (res.data?.success) {
        toast.success(res.data.message || `Reconciliation for event #${reconEvent.id} completed successfully!`);
        setReconModalOpen(false);
        setReconEvent(null);
        await fetchEvents();
      } else {
        toast.error(res.data?.message || 'Reconciliation failed');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to execute reconciliation';
      toast.error(errMsg);
    } finally {
      setIsReconciling(false);
    }
  };

  // Execute Batch Recovery for Stale Leases
  const handleExecuteBatchRecover = async () => {
    setIsBatchRecovering(true);
    try {
      const res = await api.post('/api/erp/outbox/recover', { limit: 25 });
      if (res.data?.success) {
        const stats = res.data.data || {};
        toast.success(
          res.data.message ||
          `Batch recovery complete: ${stats.reclaimed || 0} reclaimed, ${stats.succeeded || 0} succeeded, ${stats.failed || 0} failed.`
        );
        setBatchModalOpen(false);
        await fetchEvents();
      } else {
        toast.error(res.data?.message || 'Batch recovery failed');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Batch recovery failed';
      toast.error(errMsg);
    } finally {
      setIsBatchRecovering(false);
    }
  };

  // Execute Batch Reconciliation (Phase 3.3)
  const handleExecuteBatchReconcile = async () => {
    setIsBatchReconciling(true);
    try {
      const res = await api.post('/api/erp/reconcile', { limit: 25 });
      if (res.data?.success) {
        const stats = res.data.data || {};
        toast.success(
          res.data.message ||
          `Batch reconciliation complete: ${stats.synced || 0} synced (${stats.repaired || 0} repaired), ${stats.not_found || 0} not in ERP.`
        );
        setBatchReconModalOpen(false);
        await fetchEvents();
      } else {
        toast.error(res.data?.message || 'Batch reconciliation failed');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Batch reconciliation failed';
      toast.error(errMsg);
    } finally {
      setIsBatchReconciling(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
      toast.success('Idempotency key copied to clipboard');
    }
  };

  // Filtered Events
  const filteredEvents = events.filter((ev) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const rxIdMatch = String(ev.prescription_id || '').includes(q);
    const evIdMatch = String(ev.id || '').includes(q);
    const patientMatch = (ev.patient_name || '').toLowerCase().includes(q);
    const keyMatch = (ev.idempotency_key || '').toLowerCase().includes(q);
    const errMatch = (ev.last_error || '').toLowerCase().includes(q);
    return rxIdMatch || evIdMatch || patientMatch || keyMatch || errMatch;
  });

  const getStatusBadge = (status, lockedUntil) => {
    const isLeaseActive = lockedUntil && new Date(lockedUntil) > new Date();
    switch (status) {
      case 'FAILED':
        return (
          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300 gap-1 font-semibold">
            <XCircle className="h-3 w-3" /> FAILED
          </Badge>
        );
      case 'REQUIRES_REVIEW':
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 gap-1 font-semibold">
            <AlertTriangle className="h-3 w-3" /> REQUIRES REVIEW
          </Badge>
        );
      case 'ERP_SUCCESS_LOCAL_INCONSISTENT':
        return (
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 gap-1 font-semibold">
            <AlertCircle className="h-3 w-3" /> INCONSISTENT
          </Badge>
        );
      case 'PROCESSING':
        return isLeaseActive ? (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 gap-1 font-semibold animate-pulse">
            <Clock className="h-3 w-3" /> PROCESSING (LOCKED)
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1 font-semibold">
            <AlertTriangle className="h-3 w-3" /> STALE LEASE
          </Badge>
        );
      case 'SUCCESS':
        return (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 gap-1 font-semibold">
            <CheckCircle2 className="h-3 w-3" /> SUCCESS
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
            {status || 'PENDING'}
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold flex items-center gap-2.5 text-slate-900">
            <RotateCcw className="h-7 w-7 text-emerald-700 flex-shrink-0" />
            ERP Recovery Console
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Monitor outbox events, reconcile directly with OneSpace ERP commercial sales, and recover failed leases.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchEvents}
            disabled={loading}
            className="h-9 gap-1.5"
            data-testid="refresh-recovery-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setBatchReconModalOpen(true)}
            disabled={loading}
            className="border-purple-300 text-purple-700 hover:bg-purple-50 h-9 gap-1.5"
            data-testid="batch-reconcile-btn"
          >
            <GitCompare className="h-4 w-4" />
            Reconcile All with ERP
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={() => setBatchModalOpen(true)}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 gap-1.5"
            data-testid="batch-recover-btn"
          >
            <Zap className="h-4 w-4" />
            Reclaim Stale Leases
          </Button>
        </div>
      </div>

      {/* Control / Filter Bar */}
      <Card className="border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by Rx #, Event #, Patient, Error, or Idempotency Key..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 text-xs sm:text-sm"
                data-testid="recovery-search-input"
              />
            </div>

            <div className="w-full sm:w-48 flex-shrink-0">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 text-xs sm:text-sm" data-testid="status-filter-select">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="PROCESSING">Processing (Stale)</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="REQUIRES_REVIEW">Requires Review</SelectItem>
                  <SelectItem value="SUCCESS">Success</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Area */}
      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-emerald-600" />
          <p className="text-sm">Loading recovery outbox events...</p>
        </Card>
      ) : filteredEvents.length === 0 ? (
        <Card className="p-12 text-center space-y-3 border-dashed">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
          <h2 className="text-lg font-serif font-bold text-slate-800">No Recovery Candidates Found</h2>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
            {searchQuery
              ? 'No outbox events match your active search filters.'
              : 'All ERP synchronization events are healthy and completed. There are currently no failed or stuck outbox events requiring attention.'}
          </p>
          {searchQuery && (
            <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
              Clear Filter
            </Button>
          )}
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block rounded-md border bg-card shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-20">Event #</TableHead>
                  <TableHead>Prescription</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="max-w-[280px]">Error Summary</TableHead>
                  <TableHead className="text-center">Retries</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((ev) => {
                  const isSuccess = ev.status === 'SUCCESS';
                  const isProcessingActive =
                    ev.status === 'PROCESSING' && ev.locked_until && new Date(ev.locked_until) > new Date();

                  return (
                    <TableRow key={ev.id} className="hover:bg-slate-50/70" data-testid={`outbox-row-${ev.id}`}>
                      <TableCell className="font-mono text-xs font-medium text-slate-600">
                        #{ev.id}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 font-medium text-sm text-slate-900">
                          <FileText className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                          <span>Rx #{ev.prescription_id}</span>
                        </div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate max-w-[160px]" title={ev.idempotency_key}>
                          {ev.idempotency_key}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-xs text-slate-800">
                          {ev.patient_name || 'Patient'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(ev.status, ev.locked_until)}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        {ev.last_error ? (
                          <div
                            className="text-xs text-rose-700 bg-rose-50/70 p-1.5 rounded border border-rose-200/60 line-clamp-2 font-mono break-all"
                            title={ev.last_error}
                          >
                            {ev.last_error}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">
                        <Badge variant="secondary" className="font-mono">
                          {ev.retry_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {ev.updated_at ? new Date(ev.updated_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-xs"
                            onClick={() => handleOpenDiagnostics(ev)}
                            title="Inspect Diagnostics"
                            data-testid={`diagnostics-btn-${ev.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Diagnostics
                          </Button>

                          {!isSuccess && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1 text-xs text-purple-700 border-purple-300 hover:bg-purple-50 font-medium"
                                onClick={() => handleOpenReconcile(ev)}
                                disabled={isProcessingActive}
                                title="Reconcile with OneSpace ERP sale"
                                data-testid={`reconcile-btn-${ev.id}`}
                              >
                                <GitCompare className="h-3.5 w-3.5" />
                                Reconcile
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-medium"
                                onClick={() => handleInitiateRecover(ev)}
                                disabled={isProcessingActive}
                                title={isProcessingActive ? 'Active lease in progress' : 'Trigger recovery'}
                                data-testid={`recover-btn-${ev.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Recover
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {filteredEvents.map((ev) => {
              const isSuccess = ev.status === 'SUCCESS';
              const isProcessingActive =
                ev.status === 'PROCESSING' && ev.locked_until && new Date(ev.locked_until) > new Date();

              return (
                <Card key={ev.id} className="border shadow-sm p-4 space-y-3" data-testid={`outbox-card-${ev.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 font-medium text-sm">
                      <FileText className="h-4 w-4 text-emerald-600" />
                      <span>Prescription #{ev.prescription_id}</span>
                      <span className="text-xs font-mono text-muted-foreground">(Event #{ev.id})</span>
                    </div>
                    {getStatusBadge(ev.status, ev.locked_until)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium text-foreground">Patient:</span> {ev.patient_name || 'Patient'}
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Retries:</span> {ev.retry_count || 0}
                    </div>
                  </div>

                  {ev.last_error && (
                    <div className="text-xs text-rose-700 bg-rose-50 p-2 rounded border border-rose-200 line-clamp-3 font-mono break-all">
                      {ev.last_error}
                    </div>
                  )}

                  <div className="text-[11px] font-mono text-muted-foreground truncate" title={ev.idempotency_key}>
                    Key: {ev.idempotency_key}
                  </div>

                  <div className="flex items-center justify-end gap-1.5 pt-1 border-t flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 text-xs"
                      onClick={() => handleOpenDiagnostics(ev)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Diagnostics
                    </Button>

                    {!isSuccess && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                          onClick={() => handleOpenReconcile(ev)}
                          disabled={isProcessingActive}
                        >
                          <GitCompare className="h-3.5 w-3.5" />
                          Reconcile
                        </Button>

                        <Button
                          variant="default"
                          size="sm"
                          className="bg-emerald-700 hover:bg-emerald-800 text-white h-8 gap-1 text-xs"
                          onClick={() => handleInitiateRecover(ev)}
                          disabled={isProcessingActive}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Recover
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* DIAGNOSTICS MODAL */}
      <Dialog open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2 text-slate-900">
              <Eye className="h-5 w-5 text-emerald-700" />
              Outbox Event Diagnostics
            </DialogTitle>
            <DialogDescription className="text-xs">
              Sanitized diagnostic information for ERP synchronization event #{selectedDiagnostics?.id}.
            </DialogDescription>
          </DialogHeader>

          {diagnosticsLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-emerald-600" />
              Loading event details...
            </div>
          ) : selectedDiagnostics ? (
            <div className="space-y-4 py-2 text-xs">
              {/* Event Overview */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border">
                <div>
                  <div className="text-muted-foreground font-medium">Prescription ID</div>
                  <div className="text-sm font-bold text-foreground">#{selectedDiagnostics.prescription_id}</div>
                </div>
                <div>
                  <div className="text-muted-foreground font-medium">Status</div>
                  <div className="mt-0.5">{getStatusBadge(selectedDiagnostics.status, selectedDiagnostics.locked_until)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground font-medium">Retry Count</div>
                  <div className="text-sm font-semibold">{selectedDiagnostics.retry_count || 0}</div>
                </div>
                <div>
                  <div className="text-muted-foreground font-medium">Patient</div>
                  <div className="text-sm font-semibold">{selectedDiagnostics.patient_name || 'Clinic Patient'}</div>
                </div>
              </div>

              {/* ERP Identifiers if synced */}
              {(selectedDiagnostics.erp_sale_id || selectedDiagnostics.erp_invoice_no) && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="font-semibold text-emerald-800 mb-1">ERP Commercial Confirmation</div>
                  <div className="grid grid-cols-2 gap-2 text-emerald-900">
                    <div>Sale ID: <strong>#{selectedDiagnostics.erp_sale_id}</strong></div>
                    <div>Invoice No: <strong>{trimInvoiceNumber(selectedDiagnostics.erp_invoice_no)}</strong></div>
                  </div>
                </div>
              )}

              {/* Idempotency Key */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-muted-foreground font-medium">
                  <span>Deterministic Idempotency Key</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedDiagnostics.idempotency_key)}
                    className="text-emerald-700 hover:text-emerald-800 flex items-center gap-1 font-sans"
                  >
                    {copiedKey ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                    <span>{copiedKey ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
                <div className="p-2 bg-slate-100 rounded border font-mono text-[11px] break-all select-all text-slate-800">
                  {selectedDiagnostics.idempotency_key}
                </div>
              </div>

              {/* Error Traceback (Sanitized) */}
              {selectedDiagnostics.last_error && (
                <div className="space-y-1">
                  <div className="text-muted-foreground font-medium flex items-center gap-1 text-rose-700">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>Last Synchronization Error (Sanitized)</span>
                  </div>
                  <div className="p-2.5 bg-rose-50 border border-rose-200 rounded font-mono text-[11px] text-rose-900 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {selectedDiagnostics.last_error}
                  </div>
                </div>
              )}

              {/* Payload Summary */}
              {selectedDiagnostics.payload_summary && (
                <div className="space-y-1">
                  <div className="text-muted-foreground font-medium">Payload Summary</div>
                  <div className="p-2.5 bg-slate-50 border rounded text-[11px] space-y-1 text-slate-700">
                    <div>Payment Mode: <strong>{selectedDiagnostics.payload_summary.paymentMode || '—'}</strong></div>
                    <div>Items: <strong>{selectedDiagnostics.payload_summary.itemCount || 0} line item(s)</strong> (Total qty: {selectedDiagnostics.payload_summary.totalQuantity || 0})</div>
                    <div>Reference: <span className="font-mono">{selectedDiagnostics.payload_summary.referenceNo || '—'}</span></div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground pt-1 border-t">
                <div>Created: {selectedDiagnostics.created_at ? new Date(selectedDiagnostics.created_at).toLocaleString() : '—'}</div>
                <div>Updated: {selectedDiagnostics.updated_at ? new Date(selectedDiagnostics.updated_at).toLocaleString() : '—'}</div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="pt-2 gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDiagnosticsOpen(false)}>
              Close
            </Button>
            {selectedDiagnostics && selectedDiagnostics.status !== 'SUCCESS' && (
              <>
                <Button
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    setDiagnosticsOpen(false);
                    handleOpenReconcile(selectedDiagnostics);
                  }}
                >
                  <GitCompare className="h-4 w-4 mr-1.5" />
                  Reconcile
                </Button>
                <Button
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => {
                    setDiagnosticsOpen(false);
                    handleInitiateRecover(selectedDiagnostics);
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  Recover Event
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RECONCILIATION MODAL (Phase 3.3) */}
      <Dialog open={reconModalOpen} onOpenChange={setReconModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2 text-purple-800">
              <GitCompare className="h-5 w-5" />
              ERP-Direct Sale Reconciliation
            </DialogTitle>
            <DialogDescription className="text-xs">
              Check OneSpace ERP direct state for Prescription #{reconEvent?.prescription_id} (Ref: AYUR-RX-{reconEvent?.prescription_id}).
            </DialogDescription>
          </DialogHeader>

          {reconLoading ? (
            <div className="py-8 text-center text-muted-foreground space-y-2">
              <RefreshCw className="h-5 w-5 animate-spin mx-auto text-purple-600" />
              <p className="text-xs">Querying OneSpace ERP commercial sales ledger...</p>
            </div>
          ) : reconPreview ? (
            <div className="space-y-3 py-2 text-xs">
              {/* Outcome Banner */}
              {reconPreview.status === 'SYNCED' && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 space-y-1">
                  <div className="font-semibold flex items-center gap-1.5 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span>Already Synchronized</span>
                  </div>
                  <p className="text-[11px]">{reconPreview.message}</p>
                </div>
              )}

              {reconPreview.status === 'ERP_SUCCESS_LOCAL_INCONSISTENT' && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-900 space-y-1.5">
                  <div className="font-semibold flex items-center gap-1.5 text-purple-800">
                    <ShieldCheck className="h-4 w-4 text-purple-600" />
                    <span>ERP Commercial Sale Confirmed (#{reconPreview.erp_sale_id})</span>
                  </div>
                  <p className="text-[11px] leading-relaxed">
                    OneSpace ERP recorded this sale (Invoice: <strong>{trimInvoiceNumber(reconPreview.erp_invoice_no)}</strong>). Click "Finalize & Repair" to link the sale and commit local inventory without sending duplicate requests.
                  </p>
                </div>
              )}

              {reconPreview.status === 'ERP_NOT_FOUND' && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 space-y-1">
                  <div className="font-semibold flex items-center gap-1.5 text-slate-700">
                    <AlertCircle className="h-4 w-4 text-slate-500" />
                    <span>No Sale in ERP Ledger</span>
                  </div>
                  <p className="text-[11px] leading-relaxed">
                    OneSpace ERP has no recorded sale for reference AYUR-RX-{reconEvent?.prescription_id}. This event can be safely dispatched via Recovery.
                  </p>
                </div>
              )}

              {reconPreview.status === 'REQUIRES_REVIEW' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 space-y-1.5">
                  <div className="font-semibold flex items-center gap-1.5 text-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span>Transaction Data Conflict Detected</span>
                  </div>
                  <p className="text-[11px]">The ERP sale data differs from the local prescription request:</p>
                  <ul className="list-disc pl-4 text-[11px] space-y-0.5 text-rose-800 font-mono">
                    {(reconPreview.discrepancies || []).map((d, idx) => (
                      <li key={idx}>{d}</li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-muted-foreground">Automated repairs are blocked to protect inventory and accounting integrity.</p>
                </div>
              )}

              {reconPreview.status === 'ERP_UNAVAILABLE' && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-900 space-y-1">
                  <div className="font-semibold flex items-center gap-1.5 text-rose-800">
                    <XCircle className="h-4 w-4 text-rose-600" />
                    <span>ERP Service Unavailable</span>
                  </div>
                  <p className="text-[11px]">{reconPreview.message}</p>
                </div>
              )}

              {/* Event Details */}
              <div className="space-y-1 text-slate-700 pt-1">
                <div>Patient: <strong>{reconEvent?.patient_name || 'Patient'}</strong></div>
                <div>Idempotency Key: <span className="font-mono text-[11px]">{reconEvent?.idempotency_key}</span></div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setReconModalOpen(false)} disabled={isReconciling}>
              Close
            </Button>
            {reconPreview?.can_repair && (
              <Button
                className="bg-purple-700 hover:bg-purple-800 text-white"
                onClick={handleExecuteReconcile}
                disabled={isReconciling}
                data-testid="confirm-repair-reconcile-btn"
              >
                {isReconciling ? 'Repairing...' : 'Finalize & Repair Local State'}
              </Button>
            )}
            {reconPreview?.status === 'ERP_NOT_FOUND' && (
              <Button
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={() => {
                  setReconModalOpen(false);
                  handleInitiateRecover(reconEvent);
                }}
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Proceed to Recover
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SINGLE EVENT RECOVERY CONFIRMATION MODAL */}
      <Dialog open={recoveryModalOpen} onOpenChange={setRecoveryModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2 text-emerald-800">
              <RotateCcw className="h-5 w-5" />
              Confirm ERP Event Recovery
            </DialogTitle>
            <DialogDescription className="text-xs">
              Trigger intentional recovery for outbox event #{eventToRecover?.id} (Prescription #{eventToRecover?.prescription_id}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 space-y-1.5">
              <div className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0" />
                <span>Deterministic Idempotency Guarantee</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                This action re-dispatches the sale request using the existing deterministic key. If OneSpace ERP has already recorded the sale, it returns the cached confirmation without issuing duplicate invoices.
              </p>
            </div>

            <div className="space-y-1 text-slate-700">
              <div>Patient: <strong>{eventToRecover?.patient_name || 'Patient'}</strong></div>
              <div>Current Status: <strong>{eventToRecover?.status}</strong></div>
              <div>Previous Retries: <strong>{eventToRecover?.retry_count || 0}</strong></div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRecoveryModalOpen(false)} disabled={isRecovering}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={handleExecuteRecover}
              disabled={isRecovering}
              data-testid="confirm-recover-modal-btn"
            >
              {isRecovering ? 'Recovering...' : 'Yes, Execute Recovery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BATCH RECONCILIATION MODAL (Phase 3.3) */}
      <Dialog open={batchReconModalOpen} onOpenChange={setBatchReconModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2 text-purple-800">
              <GitCompare className="h-5 w-5" />
              Batch Reconcile with OneSpace ERP
            </DialogTitle>
            <DialogDescription className="text-xs">
              Directly reconcile all actionable outbox events against OneSpace ERP commercial sales.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <p className="text-slate-600 leading-relaxed">
              This action queries OneSpace ERP for all actionable events. For each event with an existing ERP commercial sale, it verifies line items and finalizes the local AyurClinic state without creating duplicate sales or double-deducting stock.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBatchReconModalOpen(false)} disabled={isBatchReconciling}>
              Cancel
            </Button>
            <Button
              className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={handleExecuteBatchReconcile}
              disabled={isBatchReconciling}
              data-testid="confirm-batch-recon-modal-btn"
            >
              {isBatchReconciling ? 'Reconciling...' : 'Reconcile with ERP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BATCH RECOVERY CONFIRMATION MODAL */}
      <Dialog open={batchModalOpen} onOpenChange={setBatchModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2 text-emerald-800">
              <Zap className="h-5 w-5" />
              Reclaim Stale Outbox Leases
            </DialogTitle>
            <DialogDescription className="text-xs">
              Reclaim and re-process all outbox events stuck in PROCESSING status whose lease has expired.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <p className="text-slate-600 leading-relaxed">
              When an application server crashes during an in-flight ERP call, the outbox event holds a lock until its lease expires (60s). This batch action reclaims up to 25 stale events and safely processes them.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBatchModalOpen(false)} disabled={isBatchRecovering}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={handleExecuteBatchRecover}
              disabled={isBatchRecovering}
              data-testid="confirm-batch-modal-btn"
            >
              {isBatchRecovering ? 'Processing...' : 'Reclaim Stale Leases'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRecoveryConsole;
